import React, { useRef, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Filter } from "lucide-react";
import { TableHead } from "./Table";
import { FilterPanel, isFilterActive } from "./FilterPanel";

/**
 * Table header cell with the events-list interaction pattern: an optional sort
 * button on the label and an optional filter icon opening a dropdown. The
 * filter descriptor is documented in FilterPanel; below `lg` the same
 * descriptors are rendered by MobileFilterBar instead.
 *
 * <TableColumnHeader
 *   label="Reason"
 *   sortDir="asc" onSort={fn}                 // sortDir null while sorted by another column
 *   filter={{ value, onChange, options, align }}
 *   filterLabel="city"                        // when the filter is not on the column's own field
 * />
 */
export function TableColumnHeader({ label, className, sortDir, onSort, filter, filterLabel = label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const filterActive = filter ? isFilterActive(filter) : false;

  return (
    <TableHead ref={ref} className={className}>
      <div className="flex items-center gap-1 whitespace-nowrap">
        {onSort ? (
          <button
            onClick={onSort}
            className="flex items-center gap-1 hover:text-foreground transition-colors -ml-1 px-1 py-0.5 rounded"
          >
            {label}
            {sortDir === "asc" ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : sortDir === "desc" ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />
            )}
          </button>
        ) : (
          <span>{label}</span>
        )}
        {filter && (
          <button
            onClick={() => setOpen((o) => !o)}
            className="p-0.5 rounded hover:bg-muted transition-colors"
            title={`Filter by ${filterLabel.toLowerCase()}`}
          >
            <Filter className={`h-3.5 w-3.5 ${filterActive ? "text-primary" : "opacity-30"}`} />
          </button>
        )}
        {filter?.multiple && filterActive && (
          <span className="text-[10px] bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center leading-none">
            {filter.value.length}
          </span>
        )}
      </div>
      {open && filter && (
        <FilterPanel filter={filter} label={filterLabel} anchorRef={ref} onClose={() => setOpen(false)} />
      )}
    </TableHead>
  );
}
